/**
 * AdminFinanceReconciliation — iter-252-v
 * Finance Reconciliation Ledger: single accountant view.
 * One row per child order with all financial dimensions + exception layer + audit actions.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { FinanceNavTabs } from './AdminFinanceRegister';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import {
  RefreshCw, Loader2, Download, Search, ChevronDown, ChevronRight,
  AlertTriangle, CheckCircle2, XCircle, Clock, Info, Shield, FileWarning,
  Truck, Banknote, Receipt, MoreHorizontal, ExternalLink, Filter,
  TrendingDown, TrendingUp, Calendar, ToggleLeft, ToggleRight, Activity,
  IndentDecrease, DollarSign
} from 'lucide-react';
import { Toaster } from '../../components/ui/sonner';

// ─── Constants ────────────────────────────────────────────────

const EXCEPTION_COLORS = {
  OFFLINE_REFUND: 'bg-red-900/40 text-red-300 border-red-700',
  COMMISSION_WAIVER: 'bg-yellow-900/40 text-yellow-300 border-yellow-700',
  MANUAL_SETTLEMENT: 'bg-blue-900/40 text-blue-300 border-blue-700',
  EARLY_LAUNCH_POLICY: 'bg-purple-900/40 text-purple-300 border-purple-700',
  SELF_MANAGED_DELIVERY: 'bg-teal-900/40 text-teal-300 border-teal-700',
  HISTORICAL_CORRECTION: 'bg-orange-900/40 text-orange-300 border-orange-700',
  SYSTEM_RECOVERY: 'bg-indigo-900/40 text-indigo-300 border-indigo-700',
};

const REFUND_SOURCE_COLORS = {
  'None': 'bg-slate-800 text-slate-400',
  'Offline Manual': 'bg-red-900/50 text-red-300',
  'Direct Razorpay': 'bg-yellow-900/50 text-yellow-300',
  'Shopify→Razorpay': 'bg-green-900/50 text-green-300',
  'Unknown': 'bg-gray-800 text-gray-400',
};

const FINANCIAL_STATUS_CONFIG = {
  paid: { color: 'bg-emerald-900/50 text-emerald-300', icon: CheckCircle2 },
  partially_paid: { color: 'bg-yellow-900/50 text-yellow-300', icon: Clock },
  refunded: { color: 'bg-red-900/50 text-red-300', icon: XCircle },
  partially_refunded: { color: 'bg-orange-900/50 text-orange-300', icon: AlertTriangle },
  voided: { color: 'bg-gray-800 text-gray-400', icon: XCircle },
};

const ORDER_STATUS_CONFIG = {
  delivered: { color: 'bg-emerald-900/50 text-emerald-300' },
  confirmed: { color: 'bg-blue-900/50 text-blue-300' },
  cancelled: { color: 'bg-red-900/50 text-red-300' },
  refunded: { color: 'bg-red-900/50 text-red-300' },
  pending: { color: 'bg-yellow-900/50 text-yellow-300' },
};

const ELIGIBILITY_CONFIG = {
  ELIGIBLE: { color: 'bg-emerald-900/50 text-emerald-300', label: 'Eligible' },
  G2_CANCELLED: { color: 'bg-red-900/50 text-red-300', label: 'Cancelled' },
  G6_NOT_DELIVERED: { color: 'bg-yellow-900/50 text-yellow-300', label: 'Not Delivered' },
  G3_NOT_PAID: { color: 'bg-orange-900/50 text-orange-300', label: 'Not Paid' },
};

// ─── Utility helpers ──────────────────────────────────────────

const fmt = (v) => v == null || v === '' ? '—' : `₹${Number(v).toFixed(2)}`;
const fmtAbs = (v) => v == null ? '—' : `₹${Math.abs(Number(v)).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

function StatusBadge({ value, config }) {
  const cfg = (config || {})[value] || { color: 'bg-slate-800 text-slate-400' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
      {cfg.label || value || '—'}
    </span>
  );
}

const AUTHORITY_SOURCE_COLORS = {
  STANDARD: 'bg-slate-800 text-slate-400',
  RAZORPAY_ROUTE: 'bg-emerald-900/40 text-emerald-300',
  COMMISSION_WAIVER: 'bg-yellow-900/40 text-yellow-300',
  OFFLINE_REFUND: 'bg-red-900/40 text-red-300',
  DIRECT_RAZORPAY_REFUND: 'bg-orange-900/40 text-orange-300',
  SHOPIFY_REFUND: 'bg-blue-900/40 text-blue-300',
  OFFLINE_SETTLEMENT: 'bg-indigo-900/40 text-indigo-300',
  SYSTEM_RECOVERY: 'bg-purple-900/40 text-purple-300',
  CANCELLED: 'bg-gray-800 text-gray-400',
  PENDING_DELIVERY: 'bg-yellow-900/20 text-yellow-500',
  REFUNDED: 'bg-red-900/30 text-red-400',
  EARLY_LAUNCH_POLICY: 'bg-teal-900/40 text-teal-300',
};

const TRANSFER_SYNC_COLORS = {
  LINKED: 'bg-emerald-900/40 text-emerald-300 border-emerald-700',
  ESCROW_HOLD: 'bg-blue-900/40 text-blue-300 border-blue-700',
  MISSING_TRANSFER_ID: 'bg-red-900/40 text-red-300 border-red-700',
  MANUAL_SETTLEMENT: 'bg-blue-900/40 text-blue-300 border-blue-700',
  NOT_RELEASED_YET: 'bg-slate-800 text-slate-400 border-slate-600',
  SETTLEMENT_BLOCKED: 'bg-red-950/50 text-red-400 border-red-800',
  SYNC_ERROR: 'bg-orange-900/40 text-orange-300 border-orange-700',
};

function VarianceCell({ value }) {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-slate-800 text-slate-400 border border-slate-600">
        <AlertTriangle size={10} />
        UNKNOWN
      </span>
    );
  }
  const abs = Math.abs(value || 0);
  const color = abs < 0.5 ? 'text-emerald-400'
    : abs < 50 ? 'text-yellow-400'
    : 'text-red-400';
  const indicator = abs < 0.5 ? '✓' : value > 0 ? '▲' : '▼';
  return (
    <span className={`text-xs tabular-nums font-mono ${color}`}>
      {indicator} {fmt(value)}
    </span>
  );
}

function ActualTransferCell({ value }) {
  if (value === null || value === undefined) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-900/40 text-amber-300 border border-amber-700">
        <AlertTriangle size={10} />
        TRANSFER UNKNOWN
      </span>
    );
  }
  return <AmountCell value={value} variant={value > 0 ? 'positive' : 'neutral'} />;
}

function TransferSyncBadge({ status }) {
  const cls = TRANSFER_SYNC_COLORS[status] || 'bg-slate-800 text-slate-400 border-slate-600';
  const label = status?.replace(/_/g, ' ') || '—';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs border whitespace-nowrap ${cls}`}>
      {label}
    </span>
  );
}

const LIFECYCLE_STAGE_COLORS = {
  PAID: 'bg-blue-950/50 text-blue-300',
  REFUNDED: 'bg-red-950/50 text-red-300',
  PARTIAL_REFUND: 'bg-orange-950/50 text-orange-300',
  DELIVERED: 'bg-emerald-950/50 text-emerald-300',
  PENDING_DELIVERY: 'bg-slate-800 text-slate-400',
  DELIVERY_BLOCKED: 'bg-red-950/30 text-red-400',
  COMMISSION_WAIVER: 'bg-purple-950/50 text-purple-300',
  OFFLINE_REFUND: 'bg-orange-950/50 text-orange-300',
  SETTLED: 'bg-emerald-900/60 text-emerald-200',
  ESCROW_HOLD: 'bg-blue-900/60 text-blue-200',
  MANUALLY_SETTLED: 'bg-cyan-950/50 text-cyan-300',
  REVERSAL_APPLIED: 'bg-emerald-950/50 text-emerald-400',
  REVERSAL_PENDING: 'bg-amber-950/50 text-amber-300',
  TRANSFER_MISSING: 'bg-red-950/50 text-red-300',
  SETTLEMENT_BLOCKED: 'bg-red-950/40 text-red-400',
  SYNC_ERROR: 'bg-orange-950/50 text-orange-300',
  CANCELLED: 'bg-slate-800 text-slate-500',
};

function LifecycleBadges({ stages }) {
  if (!stages || stages.length === 0) return <span className="text-slate-600 text-xs">—</span>;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {stages.map((stage, i) => (
        <span key={stage} className="flex items-center gap-0.5">
          {i > 0 && <span className="text-slate-700 text-xs">›</span>}
          <span className={`px-1 py-0.5 rounded text-xs whitespace-nowrap ${LIFECYCLE_STAGE_COLORS[stage] || 'bg-slate-800 text-slate-400'}`}>
            {stage.replace(/_/g, ' ')}
          </span>
        </span>
      ))}
    </div>
  );
}

function ParentGroupRow({ parentKey, children, expandedGroups, isCollapsed, onToggle }) {
  const totalGross = children.reduce((s, r) => s + (r.gross || 0), 0);
  const totalComm = children.reduce((s, r) => s + (r.commission || 0), 0);
  const totalPayout = children.reduce((s, r) => s + (r.calculated_payout || 0), 0);
  const unknownCount = children.filter(r => r.actual_transfer === null || r.actual_transfer === undefined).length;
  const excCount = children.filter(r => r.financial_exception).length;
  const vendors = [...new Set(children.map(r => r.vendor_name).filter(Boolean))];

  return (
    <tr className="bg-slate-950 hover:bg-slate-900/60 border-b border-slate-800 cursor-pointer"
      onClick={() => onToggle(parentKey)}
      data-testid={`parent-row-${parentKey}`}>
      <td colSpan={100} className="px-3 py-2.5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-slate-400 flex-shrink-0">
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </span>
          <span className="font-mono font-semibold text-sm text-white">{parentKey}</span>
          <span className="bg-slate-800 border border-slate-700 text-slate-400 text-xs px-1.5 py-0.5 rounded">
            {children.length} orders
          </span>
          <span className="text-slate-500 text-xs">
            {vendors.length <= 2 ? vendors.join(', ') : `${vendors.length} vendors`}
          </span>
          <div className="flex items-center gap-4 ml-auto flex-wrap">
            <span className="text-white text-xs font-mono">Gross {fmt(totalGross)}</span>
            <span className="text-yellow-400 text-xs font-mono">Comm {fmt(totalComm)}</span>
            <span className="text-emerald-400 text-xs font-mono">Payout {fmt(totalPayout)}</span>
            {unknownCount > 0 && (
              <span className="text-amber-400 text-xs flex items-center gap-1">
                <AlertTriangle size={11} /> {unknownCount} Transfer Unknown
              </span>
            )}
            {excCount > 0 && (
              <span className="text-yellow-400 text-xs">{excCount} exception{excCount > 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function AmountCell({ value, variant = 'neutral', dim = false }) {
  const base = 'text-xs tabular-nums font-mono';
  const clr = variant === 'positive' ? 'text-emerald-400'
    : variant === 'negative' ? 'text-red-400'
    : variant === 'warning' ? 'text-yellow-400'
    : 'text-slate-300';
  return (
    <span className={`${base} ${clr} ${dim ? 'opacity-50' : ''}`}>
      {fmt(value)}
    </span>
  );
}

// Shows calc_payout — "No Record" when no vendor_payouts exists, ₹0 only when VP exists but payout is genuinely zero
function PayoutCell({ row }) {
  if (!row.has_payout_record) {
    return (
      <span className="text-slate-600 text-xs italic"
        title="No vendor_payouts record — payout computation not run for this order">
        No Record
      </span>
    );
  }
  return <AmountCell value={row.calculated_payout} variant={row.calculated_payout > 0 ? 'positive' : 'neutral'} />;
}

// ─── Formula Flag inline badge ────────────────────────────────

function FormulaFlag({ flags }) {
  if (!flags || flags.length === 0) return null;
  return (
    <span title={flags.map(f => `${f.field}: expected ${f.expected}, got ${f.actual} (Δ ${f.delta})`).join('\n')}
      className="inline-flex items-center gap-0.5 text-yellow-400 cursor-help ml-1">
      <AlertTriangle size={12} />
      <span className="text-xs">{flags.length}</span>
    </span>
  );
}

const REFUND_STATUS_CONFIG = {
  'Refunded': 'bg-red-900/50 text-red-300',
  'Partial Refund': 'bg-orange-900/50 text-orange-300',
  'Refund Record Exists': 'bg-yellow-900/30 text-yellow-400',
  'Not Refunded': 'bg-slate-800 text-slate-500',
};

function RefundStatusCell({ status }) {
  const cls = REFUND_STATUS_CONFIG[status] || 'bg-slate-800 text-slate-500';
  return (
    <span className={`px-2 py-0.5 rounded text-xs whitespace-nowrap ${cls}`}>
      {status || 'Not Refunded'}
    </span>
  );
}

// REVERSAL_PENDING banner — shown when offline refund exception was recorded
// but the reversal writer (apply_refund_reversal) has not yet been triggered.
function ReversalPendingBadge() {
  return (
    <span
      data-testid="reversal-pending-badge"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold
                 bg-orange-950/60 text-orange-300 border border-orange-700/60 whitespace-nowrap"
      title="Offline refund exception recorded. Run 'Apply Refund Reversal' to update financial totals."
    >
      <AlertTriangle size={10} />
      REVERSAL PENDING
    </span>
  );
}

// Calculation Engine badge — V2 (canonical) vs LEGACY vs other
const CALC_ENGINE_CFG = {
  V2:           { cls: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/50', label: 'V2' },
  LEGACY:       { cls: 'bg-amber-900/40 text-amber-300 border-amber-700/50',       label: 'LEGACY' },
  RECALCULATED: { cls: 'bg-blue-900/40 text-blue-300 border-blue-700/50',          label: 'RECALC' },
  MANUAL:       { cls: 'bg-slate-700/60 text-slate-300 border-slate-600',           label: 'MANUAL' },
  UNKNOWN:      { cls: 'bg-slate-800 text-slate-500 border-slate-700',              label: '?' },
};

function CalcEngineBadge({ engine }) {
  const cfg = CALC_ENGINE_CFG[engine] || CALC_ENGINE_CFG.UNKNOWN;
  return (
    <span
      data-testid="calc-engine-badge"
      className={`px-1.5 py-0.5 rounded text-xs font-mono font-bold border ${cfg.cls}`}
      title={`Calculation Engine: ${engine || 'UNKNOWN'}`}
    >
      {cfg.label}
    </span>
  );
}

// Phase 7 — Commission Source Label
// Maps internal source codes to user-facing labels (no technical badges).
function CommissionSourceLabel({ source }) {
  if (!source) return <span className="text-slate-500 text-xs">—</span>;
  const s = source.toString();
  if (s.startsWith('override:') || s === 'override') {
    return <span className="text-xs text-orange-400 font-medium">Custom Override</span>;
  }
  if (s === 'MISSING_PLAN') {
    return <span className="text-xs text-red-400 font-medium">Missing Plan</span>;
  }
  if (s === 'subscription_stale') {
    return <span className="text-xs text-amber-400 font-medium">Subscription (stale)</span>;
  }
  if (s.includes('migration_')) {
    return <span className="text-xs text-blue-400 font-medium">Subscription (migrated)</span>;
  }
  // subscription, subscription_history, plan, etc.
  return <span className="text-xs text-green-400 font-medium">Subscription</span>;
}

// ─── Action Modal ─────────────────────────────────────────────

function ActionModal({ order, action, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  const [meta, setMeta] = useState({});

  const actionLabels = {
    mark_refunded_offline: 'Record Offline Refund Exception',
    mark_settled_offline: 'Mark Settled (Offline)',
    apply_commission_waiver: 'Apply Commission Waiver',
    apply_refund_reversal: 'Apply Refund Reversal',
    apply_adjustment: 'Apply Adjustment',
    mark_delivery_verified: 'Verify Delivery',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl">
        <h3 className="text-white font-semibold text-lg mb-1">{actionLabels[action] || action}</h3>
        <p className="text-slate-400 text-sm mb-4">
          Order <span className="text-white font-mono">{order?.order_number}</span>
        </p>

        {action === 'apply_refund_reversal' && (
          <div className="mb-3">
            <label className="text-slate-400 text-xs mb-1 block">Refund Amount (₹)</label>
            <input
              type="number"
              className="w-full bg-slate-800 border border-slate-600 text-white rounded px-3 py-2 text-sm"
              placeholder={`${order?.gross || ''}`}
              onChange={e => setMeta(m => ({ ...m, refund_amount: parseFloat(e.target.value) }))}
            />
          </div>
        )}

        <div className="mb-4">
          <label className="text-slate-400 text-xs mb-1 block">Reason (required)</label>
          <textarea
            className="w-full bg-slate-800 border border-slate-600 text-white rounded px-3 py-2 text-sm resize-none"
            rows={3}
            placeholder="Explain why this action is being taken..."
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </div>

        <p className="text-slate-500 text-xs mb-4">
          This action writes to the audit log and cannot be undone. Source records are not edited.
        </p>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-slate-400 hover:text-white">Cancel</Button>
          <Button
            onClick={() => onSubmit(action, order.order_id, reason, meta)}
            disabled={!reason.trim() || loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="action-confirm-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Refund Reversal Audit Panel ──────────────────────────────

const AUTHORITY_STATUS_CONFIG = {
  COMPLETE:       { bg: 'bg-emerald-900/30 border-emerald-700', text: 'text-emerald-300', label: 'COMPLETE' },
  PARTIAL:        { bg: 'bg-yellow-900/30 border-yellow-700',   text: 'text-yellow-300',  label: 'PARTIAL' },
  MISSING:        { bg: 'bg-red-900/30 border-red-700',         text: 'text-red-300',     label: 'MISSING' },
  NOT_APPLICABLE: { bg: 'bg-slate-800 border-slate-700',        text: 'text-slate-500',   label: 'N/A' },
};

function ReversalAuditPanel({ audit }) {
  if (!audit) return null;

  const status = audit.authority_status || 'NOT_APPLICABLE';
  const cfg = AUTHORITY_STATUS_CONFIG[status] || AUTHORITY_STATUS_CONFIG.NOT_APPLICABLE;

  return (
    <div className="mt-1" data-testid="reversal-audit-panel">
      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
        Refund Reversal Audit
      </p>

      {/* Authority status badge */}
      <div className={`flex items-center justify-between px-3 py-2 rounded-lg border mb-3 ${cfg.bg}`}>
        <span className="text-slate-400 text-xs">Reversal Authority</span>
        <span className={`text-xs font-bold font-mono tracking-wider ${cfg.text}`} data-testid="reversal-authority-status">
          {cfg.label}
        </span>
      </div>

      {/* Per-type table */}
      {audit.items && audit.items.length > 0 && (
        <div className="rounded-lg border border-slate-700 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800/60">
                <th className="text-left px-3 py-1.5 text-slate-400 font-medium">Type</th>
                <th className="text-center px-3 py-1.5 text-slate-400 font-medium">Status</th>
                <th className="text-right px-3 py-1.5 text-slate-400 font-medium">Source</th>
              </tr>
            </thead>
            <tbody>
              {audit.items.map((item, i) => (
                <tr
                  key={item.type}
                  className={`border-b border-slate-800 last:border-0 ${item.present ? '' : 'bg-red-950/20'}`}
                  data-testid={`reversal-item-${item.type}`}
                >
                  <td className="px-3 py-1.5 text-slate-300">{item.label}</td>
                  <td className="px-3 py-1.5 text-center">
                    {item.present
                      ? <CheckCircle2 size={12} className="text-emerald-400 inline" />
                      : <XCircle size={12} className={audit.applicable ? 'text-red-400 inline' : 'text-slate-600 inline'} />
                    }
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-400">
                    {item.source === 'missing'
                      ? <span className="text-red-400/80">missing</span>
                      : <span className="text-slate-300">{item.source}</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Missing list */}
      {audit.missing && audit.missing.length > 0 && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-red-950/20 border border-red-800/40">
          <p className="text-red-400 text-xs font-semibold mb-1">Missing Reversals:</p>
          {audit.missing.map(m => (
            <div key={m} className="text-red-300/80 text-xs flex items-center gap-1.5 mb-0.5" data-testid={`missing-reversal-${m}`}>
              <span className="text-red-500">•</span> {m}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Payout Authority Panel ──────────────────────────────────
// Shows the exact calculation inputs from vendor_payouts.breakdown.
// For V2 rows: full audit trail. For LEGACY rows: shows warning + inferred rates.

const AUTHORITY_INTEGRITY_CFG = {
  CANONICAL: { cls: 'text-emerald-400', label: 'CANONICAL' },
  DERIVED:   { cls: 'text-yellow-400',  label: 'DERIVED'   },
  MISSING:   { cls: 'text-red-400',     label: 'MISSING'   },
};

function PayoutAuthorityPanel({ row }) {
  const engine = row.calculation_engine || 'UNKNOWN';
  const integrity = row.authority_integrity || 'MISSING';
  const integrityCfg = AUTHORITY_INTEGRITY_CFG[integrity] || AUTHORITY_INTEGRITY_CFG.MISSING;
  const engineCfg = CALC_ENGINE_CFG[engine] || CALC_ENGINE_CFG.UNKNOWN;
  const fmtPct = v => v != null ? `${(v * 100).toFixed(2)}%` : '—';
  const fmt = v => v != null && v !== 0 ? `₹${Number(v).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : '—';

  const rateMap = row.breakdown_rate_sources || {};

  return (
    <div data-testid="payout-authority-panel">
      <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">
        Payout Authority
      </p>

      <div className="rounded-lg border border-slate-700 overflow-hidden">
        {/* Engine + integrity header */}
        <div className="flex items-center justify-between px-3 py-2 bg-slate-800/60 border-b border-slate-700">
          <span className="text-slate-400 text-xs">Calculation Engine</span>
          <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold border ${engineCfg.cls}`}>
            {engine}
          </span>
        </div>
        <div className="flex items-center justify-between px-3 py-2 bg-slate-800/40 border-b border-slate-700">
          <span className="text-slate-400 text-xs">Authority Integrity</span>
          <span className={`text-xs font-bold font-mono ${integrityCfg.cls}`}>{integrityCfg.label}</span>
        </div>

        {/* Commission authority */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
          <span className="text-slate-400 text-xs">Commission Rate</span>
          <span className="text-white text-xs font-mono">{fmtPct(row.breakdown_commission_rate ?? row.commission_rate)}</span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
          <span className="text-slate-400 text-xs">Commission Source</span>
          <CommissionSourceLabel source={row.breakdown_commission_rate_source ?? row.commission_rate_source} />
        </div>

        {/* Order decomposition */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
          <span className="text-slate-400 text-xs">Product Price (P)</span>
          <span className="text-white text-xs font-mono">{fmt(row.payout_product_price)}</span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
          <span className="text-slate-400 text-xs">Shipping Base (S)</span>
          <span className="text-white text-xs font-mono">{fmt(row.payout_shipping_base)}</span>
        </div>
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-800">
          <span className="text-slate-400 text-xs">Shipping Mode</span>
          <span className="text-slate-300 text-xs capitalize">{row.payout_shipping_mode || '—'}</span>
        </div>

        {/* Rate sources */}
        {Object.keys(rateMap).length > 0 && (
          <>
            <div className="px-3 py-1.5 bg-slate-800/30 border-t border-slate-700">
              <span className="text-slate-500 text-xs font-semibold">Rate Sources</span>
            </div>
            {Object.entries(rateMap).filter(([k]) => !['is_resident','country_code'].includes(k)).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-1 border-b border-slate-800 last:border-0">
                <span className="text-slate-400 text-xs capitalize">{k.replace(/_/g, ' ')}</span>
                <span className="text-slate-300 text-xs font-mono">{String(v)}</span>
              </div>
            ))}
          </>
        )}
      </div>

      {/* LEGACY warning */}
      {engine === 'LEGACY' && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-amber-950/30 border border-amber-700/50">
          <p className="text-amber-400 text-xs font-semibold mb-1">Legacy Calculation Warning</p>
          <p className="text-amber-300/80 text-xs">
            This row was computed by the legacy engine. TCS rate was ~1.0% (V2: 0.5%).
            Commission was applied on gross (V2: product price only). Rates not stored.
          </p>
        </div>
      )}
    </div>
  );
}

// ─── Row Detail Panel ─────────────────────────────────────────

function RowDetailPanel({ row, onClose, onAction, actionLoading }) {
  if (!row) return null;

  const sections = [
    {
      title: 'Identity',
      items: [
        { label: 'Order', value: row.order_number },
        { label: 'Date', value: fmtDate(row.order_date) },
        { label: 'Vendor', value: row.vendor_name },
        { label: 'Shopify ID', value: row.shopify_order_id },
        { label: 'Razorpay Payment', value: row.razorpay_payment_id },
      ],
    },
    {
      title: 'Revenue',
      items: [
        { label: 'Gross', value: fmt(row.gross) },
        { label: 'Discount', value: fmt(row.discount) },
        { label: 'Net Sale', value: fmt(row.net_sale) },
      ],
    },
    {
      title: 'Deductions',
      items: [
        { label: 'Commission', value: fmt(row.commission) },
        { label: 'Commission GST', value: fmt(row.commission_gst) },
        { label: 'TDS', value: fmt(row.tds) },
        { label: 'TCS', value: fmt(row.tcs) },
        { label: 'PG Fee', value: fmt(row.pg_fee) },
        { label: 'PG GST', value: fmt(row.pg_gst) },
        { label: 'Shipping', value: fmt(row.shipping_cost) },
      ],
    },
    {
      title: 'Reversal Accounting',
      items: [
        { label: 'Commission Reversal', value: fmt(row.commission_reversal) },
        { label: 'Comm GST Reversal', value: fmt(row.commission_gst_reversal) },
        { label: 'TDS Reversal', value: fmt(row.tds_reversal) },
        { label: 'TCS Reversal', value: fmt(row.tcs_reversal) },
        { label: 'Total Refund Impact', value: fmt(row.refund_impact) },
      ],
    },
    {
      title: 'Settlement',
      items: [
        { label: 'Calculated Payout', value: fmt(row.calculated_payout) },
        { label: 'Actual Transfer', value: row.actual_transfer != null ? fmt(row.actual_transfer) : 'TRANSFER UNKNOWN' },
        { label: 'Variance', value: row.variance != null ? fmt(row.variance) : 'UNKNOWN' },
        { label: 'Variance Reason', value: row.variance_reason || '—' },
        { label: 'Transfer Sync', value: row.transfer_sync_status || '—' },
        { label: 'Net Position', value: fmt(row.net_position) },
        { label: 'Status', value: row.settlement_status },
        { label: 'Authority', value: row.settlement_authority },
        { label: 'Transfer ID', value: row.razorpay_transfer_id || '—' },
      ],
    },
    {
      title: 'Exception Layer',
      items: [
        { label: 'Refund Source', value: row.refund_source },
        { label: 'Financial Exception', value: row.financial_exception || '—' },
        { label: 'Exception Reason', value: row.financial_exception_reason || '—' },
        { label: 'Delivery Authority', value: row.delivery_authority },
        { label: 'Override By', value: row.override_user || '—' },
        { label: 'Override Date', value: row.override_date ? fmtDate(row.override_date) : '—' },
      ],
    },
  ];

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1" onClick={onClose} />
      <div className="w-full max-w-md bg-slate-900 border-l border-slate-700 overflow-y-auto shadow-2xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
          <div>
            <span className="font-mono text-white font-semibold">{row.order_number}</span>
            <span className="text-slate-500 text-sm ml-2">{row.vendor_name}</span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <XCircle size={18} />
          </button>
        </div>

        <div className="p-4 space-y-5 flex-1">
          {sections.map(sec => (
            <div key={sec.title}>
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">{sec.title}</p>
              <div className="space-y-1">
                {sec.items.map(item => (
                  <div key={item.label} className="flex justify-between text-sm">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-white font-mono text-xs">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Reversal Audit — shown for all rows (NOT_APPLICABLE collapses gracefully) */}
          <ReversalAuditPanel audit={row.reversal_audit} />

          {/* Payout Authority — shows V2/Legacy engine metadata and exact breakdown inputs */}
          <PayoutAuthorityPanel row={row} />

          {row.formula_flags && row.formula_flags.length > 0 && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-3">
              <p className="text-yellow-400 text-xs font-semibold mb-2">Formula Validation Failures</p>
              {row.formula_flags.map((f, i) => (
                <div key={i} className="text-xs text-yellow-300 mb-1">
                  <span className="font-mono">{f.field}</span>: expected {fmt(f.expected)}, got {fmt(f.actual)}
                  <span className="text-yellow-500 ml-1">(Δ {fmt(f.delta)})</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Row Actions */}
        <div className="p-4 border-t border-slate-700 space-y-2">
          <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Actions</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { action: 'mark_refunded_offline', label: 'Record Offline Refund Exception', icon: XCircle, show: true },
              { action: 'mark_settled_offline', label: 'Mark Settled (Offline)', icon: Banknote, show: true },
              { action: 'apply_commission_waiver', label: 'Commission Waiver', icon: Receipt, show: true },
              { action: 'apply_refund_reversal', label: 'Apply Reversal', icon: Shield, show: true },
              { action: 'apply_adjustment', label: 'Apply Adjustment', icon: FileWarning, show: true },
              { action: 'mark_delivery_verified', label: 'Verify Delivery', icon: Truck, show: true },
            ].filter(a => a.show).map(a => (
              <Button
                key={a.action}
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:text-white hover:border-slate-400 flex items-center gap-1.5 text-xs"
                onClick={() => onAction(a.action, row)}
                data-testid={`action-${a.action}`}
              >
                <a.icon size={12} />
                {a.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Health Banner ────────────────────────────────────────────

function HealthBanner({ health, loading }) {
  if (loading) return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mb-4 flex items-center gap-3">
      <Loader2 size={16} className="animate-spin text-slate-500" />
      <span className="text-slate-400 text-sm">Loading health metrics...</span>
    </div>
  );
  if (!health) return null;

  const s = health.summary || {};
  const hs = s.health_score ?? 100;
  const hsColor = hs >= 80 ? 'text-emerald-400' : hs >= 50 ? 'text-yellow-400' : 'text-red-400';
  const hsBg = hs >= 80 ? 'bg-emerald-950/40 border-emerald-700' : hs >= 50 ? 'bg-yellow-950/40 border-yellow-700' : 'bg-red-950/40 border-red-700';

  const riskTiles = [
    {
      label: 'Transfer Unknown', value: s.transfer_unknown ?? 0, icon: AlertTriangle,
      color: s.transfer_unknown > 0 ? 'text-red-400' : 'text-slate-400',
      bg: s.transfer_unknown > 0 ? 'bg-red-950/50 border-red-800' : 'bg-slate-800 border-slate-700',
      critical: true,
    },
    {
      label: 'Escrow Hold', value: s.escrow_hold ?? 0, icon: Clock,
      color: 'text-blue-400', bg: 'bg-blue-950/30 border-blue-800',
    },
    {
      label: 'Variance', value: s.variance ?? 0, icon: AlertTriangle,
      color: s.variance > 0 ? 'text-red-400' : 'text-emerald-400',
      bg: s.variance > 0 ? 'bg-red-950/40 border-red-800' : 'bg-slate-800 border-slate-700',
    },
    {
      label: 'Exceptions', value: s.exceptions ?? 0, icon: FileWarning,
      color: s.exceptions > 0 ? 'text-yellow-400' : 'text-slate-400',
      bg: s.exceptions > 0 ? 'bg-yellow-950/40 border-yellow-800' : 'bg-slate-800 border-slate-700',
    },
    {
      label: 'Missing Reversals', value: s.missing_reversals ?? 0, icon: XCircle,
      color: s.missing_reversals > 0 ? 'text-red-400' : 'text-emerald-400',
      bg: s.missing_reversals > 0 ? 'bg-red-950/30 border-red-800' : 'bg-slate-800 border-slate-700',
    },
    {
      label: 'Balanced', value: s.balanced ?? 0, icon: CheckCircle2,
      color: 'text-emerald-400', bg: 'bg-emerald-950/40 border-emerald-800',
    },
    {
      label: 'Total Orders', value: s.total_orders ?? 0, icon: Receipt,
      color: 'text-white', bg: 'bg-slate-800 border-slate-700',
    },
  ];

  const manualTiles = [
    { label: 'Manual Deliveries', value: s.manual_deliveries ?? 0, icon: Truck, color: 'text-cyan-400', bg: 'bg-cyan-950/30 border-cyan-800' },
    { label: 'Manual Settlements', value: s.manual_settlements ?? 0, icon: Banknote, color: 'text-indigo-400', bg: 'bg-indigo-950/30 border-indigo-800' },
    { label: 'Offline Refunds', value: s.offline_refunds ?? 0, icon: TrendingDown, color: 'text-orange-400', bg: 'bg-orange-950/30 border-orange-800' },
  ];

  return (
    <div className="mb-4" data-testid="health-banner">
      {/* Health Score + Risk Tiles row */}
      <div className="flex items-center gap-2 mb-2">
        <Activity size={14} className="text-slate-400" />
        <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">Reconciliation Health</span>
        <span className="text-slate-600 text-xs">
          {health.period?.start?.slice(0,10)} – {new Date().toISOString().slice(0,10)}
        </span>
      </div>

      <div className="flex gap-2 flex-wrap">
        {/* Health Score — prominent first tile */}
        <div className={`${hsBg} border rounded-xl p-3 flex flex-col gap-1 min-w-28`}
          data-testid="health-score-tile">
          <div className="flex items-center gap-1.5">
            <Shield size={11} className={hsColor} />
            <span className="text-slate-500 text-xs">Health Score</span>
          </div>
          <span className={`text-2xl font-bold tabular-nums ${hsColor}`}>{hs.toFixed(1)}%</span>
          <span className="text-slate-600 text-xs">{s.healthy_orders ?? 0} / {s.total_orders ?? 0} healthy</span>
        </div>

        {/* Risk tiles */}
        <div className="grid grid-cols-3 md:grid-cols-7 gap-2 flex-1">
          {riskTiles.map(t => (
            <div key={t.label}
              className={`${t.bg} border rounded-xl p-3 flex flex-col gap-1 ${t.critical && (s.transfer_unknown > 0) ? 'ring-1 ring-red-700' : ''}`}
              data-testid={`health-tile-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-1.5">
                <t.icon size={11} className={t.color} />
                <span className="text-slate-500 text-xs truncate">{t.label}</span>
              </div>
              <span className={`text-2xl font-bold tabular-nums ${t.color}`}>{t.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Manual Process KPI row */}
      <div className="mt-2">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-slate-600 text-xs font-semibold uppercase tracking-wider">Manual Process Monitoring</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {manualTiles.map(t => (
            <div key={t.label}
              className={`${t.bg} border rounded-lg px-4 py-2 flex items-center gap-3`}
              data-testid={`manual-kpi-${t.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <t.icon size={14} className={t.color} />
              <div>
                <p className="text-slate-500 text-xs">{t.label}</p>
                <p className={`text-lg font-bold tabular-nums ${t.color}`}>{t.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── GST Value Display ────────────────────────────────────────

function GstValueDisplay({ gstData, label }) {
  if (!gstData) return <span className="text-slate-500 text-xs">—</span>;
  if (gstData.state === 'valid_amount') {
    return <span className="text-white font-mono tabular-nums">{fmt(gstData.amount)}</span>;
  }
  if (gstData.state === 'NOT_YET_RECOGNIZED') {
    return (
      <span className="text-amber-400 text-xs flex items-center gap-1">
        <Clock size={11} /> Not Yet Recognized
      </span>
    );
  }
  return (
    <span className="text-slate-500 text-xs flex items-center gap-1">
      <AlertTriangle size={11} /> Not Available
    </span>
  );
}


// ─── Period Summary Panel ─────────────────────────────────────

function PeriodSummaryPanel({ summary, loading, expanded, onToggle, reportingBasis, periodLabel }) {
  if (loading) return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mb-4 flex items-center gap-3">
      <Loader2 size={14} className="animate-spin text-slate-500" />
      <span className="text-slate-400 text-sm">Building period summary...</span>
    </div>
  );

  const s = summary?.summary || {};
  const basisLabel = reportingBasis === 'cash' ? 'Cash Basis' : 'Accrual Basis';

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl mb-4" data-testid="period-summary-panel">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors rounded-xl"
        onClick={onToggle}
        data-testid="period-summary-toggle">
        <div className="flex items-center gap-2">
          <Receipt size={14} className="text-blue-400" />
          <span className="text-white font-semibold text-sm">Period Summary</span>
          <span className="text-slate-500 text-xs">{periodLabel}</span>
          <span className={`text-xs px-2 py-0.5 rounded border ${reportingBasis === 'cash' ? 'border-emerald-700 text-emerald-400 bg-emerald-950/30' : 'border-blue-700 text-blue-400 bg-blue-950/30'}`}>
            {basisLabel}
          </span>
          {s.order_count != null && (
            <span className="text-slate-600 text-xs">{s.order_count} orders</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {summary && (
            <span className="text-emerald-400 font-mono text-sm font-semibold">{fmt(s.net_sales)}</span>
          )}
          {expanded ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </div>
      </button>

      {expanded && summary && (
        <div className="border-t border-slate-700 p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

            {/* Revenue block */}
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Revenue</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Gross Sales</span>
                  <span className="text-white font-mono">{fmt(s.gross_sales)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Refunds</span>
                  <span className="text-red-400 font-mono">–{fmt(s.refunds)}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-slate-700 pt-1.5">
                  <span className="text-slate-300 font-semibold">Net Sales</span>
                  <span className="text-emerald-400 font-mono font-semibold">{fmt(s.net_sales)}</span>
                </div>
              </div>
            </div>

            {/* Platform Deductions block */}
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Platform Deductions</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Commission</span>
                  <span className="text-blue-300 font-mono">{fmt(s.commission)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">Commission GST</span>
                  <span className="text-blue-300 font-mono">{fmt(s.commission_gst)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">TDS (194O)</span>
                  <span className="text-purple-300 font-mono">{fmt(s.tds)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">TCS (52)</span>
                  <span className="text-purple-300 font-mono">{fmt(s.tcs)}</span>
                </div>
              </div>
            </div>

            {/* PG & Settlement block */}
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">Gateway & Settlement</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">PG Fees</span>
                  <span className="text-slate-300 font-mono">{fmt(s.pg_fees)}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-400">PG GST</span>
                  <span className="text-slate-300 font-mono">{fmt(s.pg_gst)}</span>
                </div>
                <div className="flex justify-between text-xs border-t border-slate-700 pt-1.5">
                  <span className="text-slate-300 font-semibold">Vendor Settlements</span>
                  <span className="text-emerald-400 font-mono font-semibold">{fmt(s.vendor_settlements)}</span>
                </div>
              </div>
            </div>

            {/* GST block */}
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
              <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-2">GST Filing</p>
              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400">Projected GST</span>
                  <GstValueDisplay gstData={s.projected_gst} label="Projected GST" />
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400">Realized GST</span>
                  <GstValueDisplay gstData={s.realized_gst} label="Realized GST" />
                </div>
                {s.projected_gst?.state === 'NOT_AVAILABLE' && (
                  <p className="text-slate-600 text-xs mt-1">GST data not yet in tax_ledger</p>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}


// ─── Period Context Bar ───────────────────────────────────────

const PERIOD_PRESETS = [
  { label: 'Current Month', value: 'current_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Current FY', value: 'current_fy' },
  { label: 'Previous FY', value: 'prev_fy' },
  { label: 'All Time', value: 'all' },
  { label: 'Custom', value: 'custom' },
];

function getPresetDates(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  // Indian FY: Apr 1 – Mar 31
  const fyStart = m >= 3 ? `${y}-04-01` : `${y - 1}-04-01`;
  const fyEnd = m >= 3 ? `${y + 1}-03-31` : `${y}-03-31`;
  const prevFyStart = m >= 3 ? `${y - 1}-04-01` : `${y - 2}-04-01`;
  const prevFyEnd = m >= 3 ? `${y}-03-31` : `${y - 1}-03-31`;

  const monthStart = `${y}-${String(m + 1).padStart(2, '0')}-01`;
  const monthEnd = new Date(y, m + 1, 0).toISOString().slice(0, 10);

  const lastM = m === 0 ? 11 : m - 1;
  const lastMY = m === 0 ? y - 1 : y;
  const lastMonthStart = `${lastMY}-${String(lastM + 1).padStart(2, '0')}-01`;
  const lastMonthEnd = new Date(lastMY, lastM + 1, 0).toISOString().slice(0, 10);

  switch (preset) {
    case 'current_month': return { start: monthStart, end: monthEnd };
    case 'last_month': return { start: lastMonthStart, end: lastMonthEnd };
    case 'current_fy': return { start: fyStart, end: fyEnd };
    case 'prev_fy': return { start: prevFyStart, end: prevFyEnd };
    case 'all': return { start: '2020-01-01', end: new Date().toISOString().slice(0, 10) };
    default: return null;
  }
}

// ─── Main Page ────────────────────────────────────────────────

export default function AdminFinanceReconciliation() {
  const { apiCall } = useAuth();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [collapsedParents, setCollapsedParents] = useState(new Set());
  const [actionModal, setActionModal] = useState({ row: null, action: null });

  // Period context
  const [periodPreset, setPeriodPreset] = useState('all');
  const [reportingBasis, setReportingBasis] = useState('accrual');

  // Filters
  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterException, setFilterException] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterTransferSync, setFilterTransferSync] = useState('');

  // Vendor list
  const [vendorList, setVendorList] = useState([]);

  // Period summary
  const [periodSummary, setPeriodSummary] = useState(null);
  const [periodSummaryLoading, setPeriodSummaryLoading] = useState(false);
  const [periodSummaryExpanded, setPeriodSummaryExpanded] = useState(true);

  // UI state
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    identity: true, state: true, revenue: true,
    deductions: true, reversal: false, gst: false,
    refund: true, settlement: true, variance: true, exception: true,
  });

  // Derive period label for display
  const periodLabel = useMemo(() => {
    const preset = PERIOD_PRESETS.find(p => p.value === periodPreset);
    if (periodPreset === 'custom' && startDate && endDate) return `${startDate} – ${endDate}`;
    return preset?.label || 'All Time';
  }, [periodPreset, startDate, endDate]);

  const fetchLedger = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: pageSize, reporting_basis: reportingBasis });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (filterStatus) params.set('financial_status', filterStatus);
      if (filterVendor) params.set('vendor_id', filterVendor);
      if (filterTransferSync) params.set('transfer_sync_filter', filterTransferSync);

      const res = await apiCall(`/api/tax-management/admin/reconciliation-ledger?${params}`);
      setData(res);
      setPage(p);
    } catch (e) {
      toast({ title: 'Error loading ledger', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [apiCall, startDate, endDate, filterStatus, filterVendor, filterTransferSync, reportingBasis, pageSize, toast]);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (filterVendor) params.set('vendor_id', filterVendor);
      const res = await apiCall(`/api/tax-management/admin/reconciliation-health?${params}`);
      setHealthData(res);
    } catch (e) {
      // Health banner is non-blocking — silently fail
    } finally {
      setHealthLoading(false);
    }
  }, [apiCall, startDate, endDate, filterVendor]);

  const fetchPeriodSummary = useCallback(async () => {
    setPeriodSummaryLoading(true);
    try {
      const params = new URLSearchParams({ reporting_basis: reportingBasis });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (filterVendor) params.set('vendor_id', filterVendor);
      const res = await apiCall(`/api/tax-management/admin/period-summary?${params}`);
      setPeriodSummary(res);
    } catch (e) {
      // Non-blocking
    } finally {
      setPeriodSummaryLoading(false);
    }
  }, [apiCall, startDate, endDate, filterVendor, reportingBasis]);

  const fetchVendorList = useCallback(async () => {
    try {
      const res = await apiCall('/api/tax-management/admin/vendor-list');
      setVendorList(res.vendors || []);
    } catch (e) {
      // Non-blocking
    }
  }, [apiCall]);

  useEffect(() => { fetchLedger(1); fetchHealth(); fetchPeriodSummary(); fetchVendorList(); }, []);

  // Re-fetch when period/basis changes (after preset applied)
  useEffect(() => {
    if (startDate || endDate || reportingBasis) {
      fetchLedger(1);
      fetchHealth();
      fetchPeriodSummary();
    }
  }, [startDate, endDate, reportingBasis]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    let rows = data.rows;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        (r.order_number || '').toLowerCase().includes(q) ||
        (r.vendor_name || '').toLowerCase().includes(q) ||
        (r.financial_exception || '').toLowerCase().includes(q)
      );
    }
    if (filterException) {
      rows = rows.filter(r => (r.financial_exception || '') === filterException);
    }
    return rows;
  }, [data?.rows, search, filterException]);

  const handleAction = async (action, row, reason, meta) => {
    setActionLoading(true);
    try {
      const payload = { order_id: row.order_id, action, reason, metadata: meta || {} };
      await apiCall('/api/tax-management/admin/reconciliation-actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      toast({ title: 'Action applied', description: `${action} recorded for ${row.order_number}` });
      setActiveAction(null);
      setSelectedRow(null);
      fetchLedger(page);
    } catch (e) {
      toast({ title: 'Action failed', description: e.message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const totals = data?.totals || {};

  const exportCSV = () => {
    if (!filteredRows.length) return;
    const headers = [
      'Order #', 'Date', 'Vendor', 'Status', 'Financial Status', 'Gross', 'Commission',
      'Comm GST', 'TDS', 'TCS', 'PG Fee', 'Shipping', 'Comm Reversal', 'Refund Impact',
      'Calc Payout', 'Actual Transfer', 'Variance', 'Variance Reason', 'Transfer Sync Status',
      'Net Position', 'Refund Source', 'Exception', 'Authority Source', 'Formula Valid',
    ];
    const rows = filteredRows.map(r => [
      r.order_number, r.order_date, r.vendor_name, r.order_status, r.financial_status,
      r.gross, r.commission, r.commission_gst, r.tds, r.tcs, r.pg_fee, r.shipping_cost,
      r.commission_reversal, r.refund_impact, r.calculated_payout,
      r.actual_transfer ?? 'TRANSFER_UNKNOWN', r.variance ?? 'UNKNOWN',
      r.variance_reason, r.transfer_sync_status, r.net_position,
      r.refund_source, r.financial_exception || '', r.authority_source, r.formula_valid,
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `reconciliation_ledger_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const exportVarianceReport = async () => {
    try {
      const params = new URLSearchParams({ variance_only: 'true' });
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (filterVendor) params.set('vendor_id', filterVendor);
      // Call backend export endpoint directly
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = sessionStorage.getItem('token') || localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/tax-management/admin/reconciliation-export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `variance_report_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast({ title: 'Variance Report exported', description: 'Rows with variance, formula errors, or exceptions included.' });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
  };

  const [openMenuId, setOpenMenuId] = useState(null);

  // Close open action menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e) => {
      if (!e.target.closest('[data-action-menu]')) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  const toggleGroup = (g) => setExpandedGroups(s => ({ ...s, [g]: !s[g] }));
  const toggleParent = (key) => setCollapsedParents(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const applyPreset = (preset) => {
    setPeriodPreset(preset);
    if (preset !== 'custom') {
      const dates = getPresetDates(preset);
      if (dates) {
        setStartDate(dates.start);
        setEndDate(dates.end);
      }
    }
  };

  const refreshAll = () => {
    fetchLedger(1);
    fetchHealth();
    fetchPeriodSummary();
  };

  // Group rows by parent_order_number for P/C hierarchy
  const groupedRenderItems = useMemo(() => {
    const groups = {};
    const standalone = [];
    filteredRows.forEach(row => {
      if (row.parent_order_number) {
        groups[row.parent_order_number] = groups[row.parent_order_number] || [];
        groups[row.parent_order_number].push(row);
      } else {
        standalone.push(row);
      }
    });
    const items = [];
    Object.entries(groups).forEach(([parentKey, children]) => {
      items.push({ type: 'parent', parentKey, children });
      if (!collapsedParents.has(parentKey)) {
        children.forEach(row => items.push({ type: 'child', row, parentKey }));
      }
    });
    standalone.forEach(row => items.push({ type: 'standalone', row }));
    return items;
  }, [filteredRows, collapsedParents]);

  return (
    <DashboardLayout>
      <Toaster />
      <div className="min-h-screen bg-slate-950 text-white">
        <div className="max-w-[1600px] mx-auto px-4 py-6">

          <FinanceNavTabs />

          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Finance Reconciliation Ledger</h1>
              <p className="text-slate-400 text-sm mt-0.5">
                {periodLabel} · <span className={reportingBasis === 'cash' ? 'text-emerald-400' : 'text-blue-400'}>{reportingBasis === 'cash' ? 'Cash Basis' : 'Accrual Basis'}</span>
              </p>
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              <Button variant="outline" size="sm"
                className="border-amber-700 text-amber-300 hover:text-white hover:border-amber-500"
                onClick={exportVarianceReport}
                data-testid="export-variance-btn">
                <AlertTriangle size={14} className="mr-1.5" /> Variance Report
              </Button>
              <Button variant="outline" size="sm"
                className="border-slate-600 text-slate-300 hover:text-white"
                onClick={exportCSV}
                data-testid="export-csv-btn">
                <Download size={14} className="mr-1.5" /> Export CSV
              </Button>
              <Button size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={refreshAll}
                disabled={loading}
                data-testid="refresh-btn">
                {loading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCw size={14} className="mr-1.5" />}
                Refresh
              </Button>
            </div>
          </div>

          {/* Period Context Controls */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-3 mb-4" data-testid="period-context-bar">
            {/* Row 1: Preset buttons + Basis toggle */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Calendar size={13} className="text-slate-500" />
                {PERIOD_PRESETS.map(p => (
                  <button key={p.value}
                    className={`px-3 py-1 rounded text-xs font-semibold transition-all border
                      ${periodPreset === p.value
                        ? 'bg-blue-700 border-blue-500 text-white shadow-sm shadow-blue-900'
                        : 'bg-transparent border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-500'}`}
                    onClick={() => applyPreset(p.value)}
                    data-testid={`period-preset-${p.value}`}>
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="h-4 w-px bg-slate-700 ml-auto" />

              {/* Reporting Basis Toggle */}
              <div className="flex items-center gap-1.5">
                <span className="text-slate-600 text-xs">Basis:</span>
                <button
                  className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                    ${reportingBasis === 'accrual' ? 'bg-blue-900/50 border-blue-700 text-blue-300' : 'bg-transparent border-slate-700 text-slate-500 hover:text-slate-300'}`}
                  onClick={() => setReportingBasis('accrual')}
                  data-testid="basis-accrual-btn"
                  title="Revenue earned date (order created_at)">
                  Accrual
                </button>
                <button
                  className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                    ${reportingBasis === 'cash' ? 'bg-emerald-900/50 border-emerald-700 text-emerald-300' : 'bg-transparent border-slate-700 text-slate-500 hover:text-slate-300'}`}
                  onClick={() => setReportingBasis('cash')}
                  data-testid="basis-cash-btn"
                  title="Actual cash movement date (paid_at)">
                  Cash
                </button>
              </div>
            </div>

            {/* Row 2: Resolved date range OR custom inputs */}
            <div className="mt-2 flex items-center gap-3">
              {periodPreset === 'custom' ? (
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 text-xs">From</span>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="bg-slate-800 border border-slate-600 text-white rounded px-2 py-1 text-xs h-7"
                    data-testid="start-date-filter" />
                  <span className="text-slate-600 text-xs">To</span>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                    className="bg-slate-800 border border-slate-600 text-white rounded px-2 py-1 text-xs h-7"
                    data-testid="end-date-filter" />
                  <Button size="sm" className="h-7 bg-blue-700 hover:bg-blue-600 text-white text-xs"
                    onClick={refreshAll}
                    data-testid="custom-apply-btn">
                    Apply Range
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-slate-600 text-xs">Showing:</span>
                  <span className="text-slate-300 text-xs font-mono">
                    {startDate ? new Date(startDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : 'All time'}
                    {startDate && endDate && (
                      <span className="text-slate-500"> → {new Date(endDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</span>
                    )}
                  </span>
                  <span className="text-slate-700 text-xs">·</span>
                  <span className={`text-xs ${reportingBasis === 'cash' ? 'text-emerald-500' : 'text-blue-500'}`}>
                    {reportingBasis === 'cash' ? 'by payment date' : 'by order date'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Accountant Period Summary */}
          <PeriodSummaryPanel
            summary={periodSummary}
            loading={periodSummaryLoading}
            expanded={periodSummaryExpanded}
            onToggle={() => setPeriodSummaryExpanded(e => !e)}
            reportingBasis={reportingBasis}
            periodLabel={periodLabel}
          />

          {/* Health Banner */}
          <HealthBanner health={healthData} loading={healthLoading} />

          {/* KPI Tiles */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
            {[
              { label: 'Gross Sales', value: fmt(totals.gross), color: 'text-white' },
              { label: 'Refund Reversals', value: fmtAbs(totals.refund_impact), color: 'text-red-400' },
              { label: 'Net Sales', value: fmt((totals.gross || 0) + (totals.refund_impact || 0)), color: 'text-emerald-300' },
              { label: 'Commission', value: fmt(totals.commission), color: 'text-blue-400' },
              { label: 'TDS + TCS', value: fmt((totals.tds || 0) + (totals.tcs || 0)), color: 'text-purple-400' },
              { label: 'PG Fees', value: fmt(totals.pg_fee), color: 'text-slate-400' },
              { label: 'Calc Payout', value: fmt(totals.calculated_payout), color: 'text-emerald-400' },
              { label: 'Variance', value: fmt(totals.variance), color: Math.abs(totals.variance || 0) < 1 ? 'text-emerald-400' : 'text-red-400' },
            ].map(tile => (
              <Card key={tile.label} className="bg-slate-900 border-slate-700">
                <CardContent className="p-3">
                  <p className="text-slate-500 text-xs mb-1">{tile.label}</p>
                  <p className={`text-base font-bold tabular-nums ${tile.color}`}>{tile.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 mb-4">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="relative flex-1 min-w-48">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  className="pl-8 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 h-8 text-sm"
                  placeholder="Search order, vendor..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </div>

              {/* Vendor Filter — first class */}
              <select value={filterVendor} onChange={e => setFilterVendor(e.target.value)}
                className="bg-slate-800 border border-slate-600 text-slate-300 rounded px-2 py-1 text-sm h-8 min-w-40"
                data-testid="vendor-filter">
                <option value="">All Vendors</option>
                {vendorList.map(v => (
                  <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>
                ))}
              </select>

              {/* Settlement Status Filter */}
              <select value={filterTransferSync} onChange={e => setFilterTransferSync(e.target.value)}
                className="bg-slate-800 border border-slate-600 text-slate-300 rounded px-2 py-1 text-sm h-8"
                data-testid="settlement-filter">
                <option value="">All Settlement Status</option>
                <option value="LINKED">Released (Linked)</option>
                <option value="ESCROW_HOLD">Escrow Hold</option>
                <option value="MANUAL_SETTLEMENT">Manual Settlement</option>
                <option value="MISSING_TRANSFER_ID">Missing Transfer ID</option>
                <option value="NOT_RELEASED_YET">Not Released Yet</option>
                <option value="SETTLEMENT_BLOCKED">Settlement Blocked</option>
                <option value="SYNC_ERROR">Sync Error</option>
              </select>

              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="bg-slate-800 border border-slate-600 text-slate-300 rounded px-2 py-1 text-sm h-8"
                data-testid="status-filter">
                <option value="">All Financial Status</option>
                <option value="paid">Paid</option>
                <option value="refunded">Refunded</option>
                <option value="partially_refunded">Partially Refunded</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select value={filterException} onChange={e => setFilterException(e.target.value)}
                className="bg-slate-800 border border-slate-600 text-slate-300 rounded px-2 py-1 text-sm h-8"
                data-testid="exception-filter">
                <option value="">All Exceptions</option>
                <option value="OFFLINE_REFUND">Offline Refund</option>
                <option value="COMMISSION_WAIVER">Commission Waiver</option>
                <option value="MANUAL_SETTLEMENT">Manual Settlement</option>
                <option value="EARLY_LAUNCH_POLICY">Early Launch Policy</option>
                <option value="HISTORICAL_CORRECTION">Historical Correction</option>
              </select>
              <Button size="sm" onClick={() => { fetchLedger(1); fetchHealth(); fetchPeriodSummary(); }}
                className="bg-slate-700 hover:bg-slate-600 text-white h-8"
                data-testid="apply-filters-btn">
                <Filter size={12} className="mr-1" /> Apply
              </Button>
            </div>

            {/* Quick filter tabs */}
            <div className="flex gap-2 mt-3 flex-wrap">
              {[
                { label: 'All', val: '' },
                { label: 'Paid', val: 'paid' },
                { label: 'Refunded', val: 'refunded' },
              ].map(f => (
                <button key={f.label}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors
                    ${filterStatus === f.val ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                  onClick={() => { setFilterStatus(f.val); fetchLedger(1); }}
                  data-testid={`quick-filter-${f.label.toLowerCase()}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Column Group Toggles */}
          <div className="flex gap-2 mb-3 flex-wrap">
            {Object.entries({
              identity: 'Identity', state: 'State', revenue: 'Revenue',
              deductions: 'Deductions', reversal: 'Reversal', gst: 'GST',
              refund: 'Refund', settlement: 'Settlement', variance: 'Variance', exception: 'Exception',
            }).map(([key, label]) => (
              <button key={key}
                onClick={() => toggleGroup(key)}
                className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors
                  ${expandedGroups[key]
                    ? 'bg-slate-700 border-slate-500 text-white'
                    : 'bg-transparent border-slate-700 text-slate-500 hover:text-slate-300'}`}
                data-testid={`toggle-group-${key}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden" data-testid="reconciliation-table">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700">
                    {expandedGroups.identity && (
                      <th colSpan={3} className="px-3 py-2 text-left text-slate-400 bg-slate-800/50 font-semibold text-xs uppercase tracking-wider">
                        Identity
                      </th>
                    )}
                    {expandedGroups.state && (
                      <th colSpan={5} className="px-3 py-2 text-center text-slate-400 bg-slate-800/30 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        State
                      </th>
                    )}
                    {expandedGroups.revenue && (
                      <th colSpan={2} className="px-3 py-2 text-center text-slate-400 bg-slate-800/50 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        Revenue
                      </th>
                    )}
                    {expandedGroups.deductions && (
                      <th colSpan={5} className="px-3 py-2 text-center text-slate-400 bg-slate-800/30 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        Deductions
                      </th>
                    )}
                    {expandedGroups.reversal && (
                      <th colSpan={3} className="px-3 py-2 text-center text-red-900/50 text-red-400 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        Reversal Accounting
                      </th>
                    )}
                    {expandedGroups.gst && (
                      <th colSpan={2} className="px-3 py-2 text-center text-slate-400 bg-slate-800/50 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        GST
                      </th>
                    )}
                    {expandedGroups.refund && (
                      <th colSpan={4} className="px-3 py-2 text-center text-red-900/60 text-red-400 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        Refund
                      </th>
                    )}
                    {expandedGroups.settlement && (
                      <th colSpan={4} className="px-3 py-2 text-center text-emerald-900/50 text-emerald-400 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        Settlement
                      </th>
                    )}
                    {expandedGroups.variance && (
                      <th colSpan={4} className="px-3 py-2 text-center text-yellow-900/50 text-yellow-400 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        System ↔ Actual
                      </th>
                    )}
                    {expandedGroups.exception && (
                      <th colSpan={2} className="px-3 py-2 text-center text-slate-400 bg-slate-800/30 font-semibold text-xs uppercase tracking-wider border-l border-slate-700">
                        Exception
                      </th>
                    )}
                    <th className="px-3 py-2 text-center text-slate-400 border-l border-slate-700 text-xs uppercase">Actions</th>
                  </tr>
                  <tr className="border-b border-slate-700/50 bg-slate-800/20">
                    {expandedGroups.identity && <>
                      <th className="px-3 py-1.5 text-left text-slate-500 font-medium whitespace-nowrap">Order #</th>
                      <th className="px-3 py-1.5 text-left text-slate-500 font-medium whitespace-nowrap">Date</th>
                      <th className="px-3 py-1.5 text-left text-slate-500 font-medium whitespace-nowrap">Vendor</th>
                    </>}
                    {expandedGroups.state && <>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Status</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Fin. Status</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Eligibility</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Financial Lifecycle</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Delivery Auth</th>
                    </>}
                    {expandedGroups.revenue && <>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Gross</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Net Sale</th>
                    </>}
                    {expandedGroups.deductions && <>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Comm</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Comm GST</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">TDS</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">TCS</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">PG Fee</th>
                    </>}
                    {expandedGroups.reversal && <>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Comm Rev.</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">TDS/TCS Rev.</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Refund Impact</th>
                    </>}
                    {expandedGroups.gst && <>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Proj GST</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">GST Drift</th>
                    </>}
                    {expandedGroups.refund && <>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Refund Status</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Refund Source</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Refund Amt</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Refund Date</th>
                    </>}
                    {expandedGroups.settlement && <>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Calc Payout</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Released</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Net Position</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Settlement</th>
                    </>}
                    {expandedGroups.variance && <>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Calc</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Actual Transfer</th>
                      <th className="px-3 py-1.5 text-right text-slate-500 font-medium whitespace-nowrap">Variance</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Variance Reason</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Authority Source</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Transfer Sync</th>
                    </>}
                    {expandedGroups.exception && <>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap border-l border-slate-700">Exception</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Authority Src</th>
                      <th className="px-3 py-1.5 text-slate-500 font-medium whitespace-nowrap">Calc Engine</th>
                    </>}
                    <th className="px-3 py-1.5 border-l border-slate-700"></th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-800">
                  {loading && !filteredRows.length ? (
                    <tr><td colSpan={30} className="text-center py-12">
                      <Loader2 className="animate-spin mx-auto text-slate-500" size={24} />
                    </td></tr>
                  ) : filteredRows.length === 0 ? (
                    <tr><td colSpan={30} className="text-center py-12 text-slate-500">No orders found</td></tr>
                  ) : groupedRenderItems.map((item, idx) => {
                    if (item.type === 'parent') {
                      return (
                        <ParentGroupRow
                          key={`parent-${item.parentKey}`}
                          parentKey={item.parentKey}
                          children={item.children}
                          expandedGroups={expandedGroups}
                          isCollapsed={collapsedParents.has(item.parentKey)}
                          onToggle={toggleParent}
                        />
                      );
                    }

                    const row = item.row;
                    const isChild = item.type === 'child';
                    const fsConfig = FINANCIAL_STATUS_CONFIG[row.financial_status] || {};
                    const osConfig = ORDER_STATUS_CONFIG[row.order_status] || { color: 'bg-slate-800 text-slate-400' };
                    const elConfig = ELIGIBILITY_CONFIG[row.payout_eligibility] || { color: 'bg-slate-800 text-slate-400', label: row.payout_eligibility };
                    const hasException = row.financial_exception;

                    return (
                      <tr key={row.order_id}
                        className={`hover:bg-slate-800/40 transition-colors cursor-pointer
                          ${isChild ? 'bg-slate-900/30' : ''}
                          ${hasException ? 'bg-amber-950/5' : ''}
                          ${row.financial_status === 'refunded' ? 'opacity-75' : ''}`}
                        onClick={() => setSelectedRow(row)}
                        data-testid={`row-${row.order_number}`}>

                        {expandedGroups.identity && <>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {isChild && <span className="text-slate-700 mr-1">└</span>}
                            <span className="font-mono text-blue-400 text-xs hover:underline">{row.order_number}</span>
                          </td>
                          <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtDate(row.order_date)}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-slate-300 text-xs truncate block max-w-28">{row.vendor_name || '—'}</span>
                          </td>
                        </>}

                        {expandedGroups.state && <>
                          <td className="px-3 py-2 border-l border-slate-800 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${osConfig.color}`}>{row.order_status || '—'}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${(fsConfig.color || 'bg-slate-800 text-slate-400')}`}>
                              {row.financial_status || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${elConfig.color}`}>{elConfig.label}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap max-w-xs">
                            <LifecycleBadges stages={row.financial_lifecycle} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-slate-400 text-xs">{row.delivery_authority || '—'}</span>
                          </td>
                        </>}

                        {expandedGroups.revenue && <>
                          <td className="px-3 py-2 text-right border-l border-slate-800">
                            <AmountCell value={row.gross} />
                            <FormulaFlag flags={row.formula_flags?.filter(f => f.field === 'gross')} />
                          </td>
                          <td className="px-3 py-2 text-right"><AmountCell value={row.net_sale} dim={row.discount > 0} /></td>
                        </>}

                        {expandedGroups.deductions && <>
                          <td className="px-3 py-2 text-right border-l border-slate-800">
                            <AmountCell value={row.commission} />
                            <FormulaFlag flags={row.formula_flags?.filter(f => f.field === 'commission')} />
                          </td>
                          <td className="px-3 py-2 text-right"><AmountCell value={row.commission_gst} /></td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={row.tds} />
                            <FormulaFlag flags={row.formula_flags?.filter(f => f.field === 'tds')} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={row.tcs} />
                            <FormulaFlag flags={row.formula_flags?.filter(f => f.field === 'tcs')} />
                          </td>
                          <td className="px-3 py-2 text-right"><AmountCell value={row.pg_fee} variant="neutral" dim /></td>
                        </>}

                        {expandedGroups.reversal && <>
                          <td className="px-3 py-2 text-right border-l border-slate-800">
                            <AmountCell value={row.commission_reversal} variant={row.commission_reversal < 0 ? 'negative' : 'neutral'} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={(row.tds_reversal || 0) + (row.tcs_reversal || 0)} variant="negative" />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={row.refund_impact} variant={row.refund_impact < 0 ? 'negative' : 'neutral'} />
                          </td>
                        </>}

                        {expandedGroups.gst && <>
                          <td className="px-3 py-2 text-right border-l border-slate-800"><AmountCell value={row.projected_gst} /></td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={row.gst_drift} variant={Math.abs(row.gst_drift || 0) > 1 ? 'warning' : 'neutral'} />
                          </td>
                        </>}

                        {expandedGroups.refund && <>
                          <td className="px-3 py-2 border-l border-slate-800 whitespace-nowrap">
                            <div className="flex flex-col gap-0.5">
                              <RefundStatusCell status={row.refund_status} />
                              {row.reversal_pending && <ReversalPendingBadge />}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${REFUND_SOURCE_COLORS[row.refund_source] || 'bg-slate-800 text-slate-400'}`}>
                              {row.refund_source || 'None'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {row.refund_amount != null
                              ? <AmountCell value={row.refund_amount} variant="negative" />
                              : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-slate-400 text-xs">{row.refund_date || '—'}</span>
                          </td>
                        </>}

                        {expandedGroups.settlement && <>
                          <td className="px-3 py-2 text-right border-l border-slate-800">
                            <PayoutCell row={row} />
                            <FormulaFlag flags={row.formula_flags?.filter(f => f.field === 'calculated_payout')} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={row.released_payout} variant={row.released_payout > 0 ? 'positive' : 'neutral'} dim={!row.released_payout} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <AmountCell value={row.net_position} variant={row.net_position > 0 ? 'positive' : row.net_position < 0 ? 'negative' : 'neutral'} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-slate-400 text-xs">{row.settlement_authority || '—'}</span>
                          </td>
                        </>}

                        {expandedGroups.variance && <>
                          <td className="px-3 py-2 text-right border-l border-slate-800">
                            <PayoutCell row={row} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <ActualTransferCell value={row.actual_transfer} />
                          </td>
                          <td className="px-3 py-2 text-right">
                            <VarianceCell value={row.variance} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-slate-400 text-xs">{row.variance_reason || '—'}</span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${AUTHORITY_SOURCE_COLORS[row.authority_source] || 'bg-slate-800 text-slate-400'}`}>
                              {row.authority_source || 'STANDARD'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <TransferSyncBadge status={row.transfer_sync_status} />
                          </td>
                        </>}

                        {expandedGroups.exception && <>
                          <td className="px-3 py-2 border-l border-slate-800 whitespace-nowrap">
                            {row.financial_exception ? (
                              <span className={`px-2 py-0.5 rounded text-xs border ${EXCEPTION_COLORS[row.financial_exception] || 'bg-slate-800 text-slate-400 border-slate-600'}`}>
                                {row.financial_exception.replace(/_/g, ' ')}
                              </span>
                            ) : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs ${AUTHORITY_SOURCE_COLORS[row.authority_source] || 'bg-slate-800 text-slate-400'}`}>
                              {row.authority_source || 'STANDARD'}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <CalcEngineBadge engine={row.calculation_engine} />
                          </td>
                        </>}

                        <td className="px-3 py-2 border-l border-slate-800" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                              onClick={() => setSelectedRow(row)}
                              title="View details"
                              data-testid={`row-detail-${row.order_number}`}>
                              <Info size={13} />
                            </button>
                            <div className="relative" data-action-menu>
                              <button
                                className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-white"
                                title="Actions"
                                onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === row.order_id ? null : row.order_id); }}
                                data-testid={`row-actions-${row.order_number}`}>
                                <MoreHorizontal size={13} />
                              </button>
                              {openMenuId === row.order_id && (
                                <div className="absolute right-0 top-6 z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-1 min-w-44">
                                  {[
                                    { action: 'mark_delivery_verified', label: 'Mark Delivered', icon: Truck },
                                    { action: 'mark_settled_offline', label: 'Mark Settled Offline', icon: Banknote },
                                  { action: 'mark_refunded_offline', label: 'Record Offline Refund Exception', icon: XCircle },
                                    { action: 'apply_commission_waiver', label: 'Apply Commission Waiver', icon: CheckCircle2 },
                                    { action: 'apply_refund_reversal', label: 'Apply Refund Reversal', icon: RefreshCw },
                                    { action: 'apply_adjustment', label: 'Apply Adjustment', icon: AlertTriangle },
                                  ].map(({ action, label, icon: Icon }) => (
                                    <button
                                      key={action}
                                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-700 text-slate-300 hover:text-white text-xs text-left"
                                      onClick={() => { setActiveAction({ action, row }); setOpenMenuId(null); }}
                                      data-testid={`action-${action}-${row.order_number}`}>
                                      <Icon size={11} />
                                      {label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>

                {/* Totals Row */}
                {filteredRows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600 bg-slate-800/60">
                      {expandedGroups.identity && <>
                        <td className="px-3 py-2 text-slate-400 text-xs font-semibold" colSpan={3}>
                          TOTALS ({data?.total_rows} orders)
                        </td>
                      </>}
                      {expandedGroups.state && <td colSpan={5} className="border-l border-slate-700" />}
                      {expandedGroups.revenue && <>
                        <td className="px-3 py-2 text-right border-l border-slate-700">
                          <span className="text-white font-mono font-semibold text-xs">{fmt(totals.gross)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-white font-mono font-semibold text-xs">{fmt(totals.net_sale)}</span>
                        </td>
                      </>}
                      {expandedGroups.deductions && <>
                        <td className="px-3 py-2 text-right border-l border-slate-700">
                          <span className="text-blue-400 font-mono font-semibold text-xs">{fmt(totals.commission)}</span>
                        </td>
                        <td className="px-3 py-2 text-right"><span className="text-blue-400 font-mono text-xs">{fmt(totals.commission_gst)}</span></td>
                        <td className="px-3 py-2 text-right"><span className="text-purple-400 font-mono text-xs">{fmt(totals.tds)}</span></td>
                        <td className="px-3 py-2 text-right"><span className="text-purple-400 font-mono text-xs">{fmt(totals.tcs)}</span></td>
                        <td className="px-3 py-2 text-right"><span className="text-slate-400 font-mono text-xs">{fmt(totals.pg_fee)}</span></td>
                      </>}
                      {expandedGroups.reversal && <>
                        <td className="px-3 py-2 text-right border-l border-slate-700">
                          <span className="text-red-400 font-mono text-xs">{fmt(totals.commission_reversal)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-red-400 font-mono text-xs">{fmt((totals.tds_reversal || 0) + (totals.tcs_reversal || 0))}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-red-400 font-mono font-semibold text-xs">{fmt(totals.refund_impact)}</span>
                        </td>
                      </>}
                      {expandedGroups.gst && <>
                        <td className="px-3 py-2 text-right border-l border-slate-700">
                          <span className="text-slate-300 font-mono text-xs">{fmt(totals.projected_gst)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-slate-300 font-mono text-xs">{fmt(totals.gst_drift)}</span>
                        </td>
                      </>}
                      {expandedGroups.refund && <td colSpan={4} className="border-l border-slate-700" />}
                      {expandedGroups.settlement && <>
                        <td className="px-3 py-2 text-right border-l border-slate-700">
                          <span className="text-emerald-400 font-mono font-semibold text-xs">{fmt(totals.calculated_payout)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-green-400 font-mono font-semibold text-xs">{fmt(totals.released_payout)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-emerald-300 font-mono font-semibold text-xs">{fmt(totals.net_position)}</span>
                        </td>
                        <td />
                      </>}
                      {expandedGroups.variance && <>
                        <td className="px-3 py-2 text-right border-l border-slate-700">
                          <span className="text-emerald-400 font-mono font-semibold text-xs">{fmt(totals.calculated_payout)}</span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <span className="text-amber-400 font-mono font-semibold text-xs">
                            {totals.actual_transfer > 0 ? fmt(totals.actual_transfer) : '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <VarianceCell value={totals.variance} />
                        </td>
                        <td colSpan={3} className="px-3 py-2 text-slate-500 text-xs">
                          {totals.transfer_unknown_count > 0 && (
                            <span className="text-amber-400">{totals.transfer_unknown_count} Transfer Unknown</span>
                          )}
                        </td>
                      </>}
                      {expandedGroups.exception && <td colSpan={2} className="border-l border-slate-700" />}
                      <td className="border-l border-slate-700" />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Pagination */}
          {data && data.total_rows > pageSize && (
            <div className="flex items-center justify-between mt-4 text-sm text-slate-400">
              <span>
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, data.total_rows)} of {data.total_rows}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline"
                  className="border-slate-600 text-slate-300"
                  disabled={page <= 1}
                  onClick={() => fetchLedger(page - 1)}
                  data-testid="prev-page-btn">
                  Previous
                </Button>
                <Button size="sm" variant="outline"
                  className="border-slate-600 text-slate-300"
                  disabled={page * pageSize >= data.total_rows}
                  onClick={() => fetchLedger(page + 1)}
                  data-testid="next-page-btn">
                  Next
                </Button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Detail Panel */}
      {selectedRow && !activeAction && (
        <RowDetailPanel
          row={selectedRow}
          onClose={() => setSelectedRow(null)}
          onAction={(action, row) => { setActiveAction({ action, row }); }}
          actionLoading={actionLoading}
        />
      )}

      {/* Action Modal */}
      {activeAction && (
        <ActionModal
          order={activeAction.row}
          action={activeAction.action}
          onClose={() => setActiveAction(null)}
          onSubmit={(action, orderId, reason, meta) => handleAction(action, activeAction.row, reason, meta)}
          loading={actionLoading}
        />
      )}
    </DashboardLayout>
  );
}
